import { useCallback, useEffect, useRef, useState } from "react";
import { activityApi } from "./activityApi";

// While something is running, a backstop under the stream.
const LIVE_POLL_MS = 5000;
// Everyone is told at once when anything changes; a burst of answers
// arrives as a burst of announcements, read once.
const SETTLE_MS = 250;

/**
 * The class's live activity, as this person may see it.
 *
 * The server builds the view per viewer — a student's own answers, released
 * marks only — so this only ever asks and holds what it is given. The
 * stream says when to ask again (`poke`); a slow poll covers a stream
 * that is down.
 *
 * `clock` is the server's time, offset for this device, so a countdown on a
 * student's phone ends when the server stops taking answers.
 */
export function useLiveActivity({ sessionGuid }) {
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const offset = useRef(0);
  const settle = useRef(null);
  const live = Boolean(sessionGuid);

  const refresh = useCallback(async () => {
    if (!live) return;
    try {
      const next = await activityApi.current(sessionGuid);
      if (next?.server_now) {
        offset.current = new Date(`${next.server_now}Z`).getTime() - Date.now();
      }
      setActivity(next);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [live, sessionGuid]);

  const poke = useCallback(() => {
    clearTimeout(settle.current);
    settle.current = setTimeout(refresh, SETTLE_MS);
  }, [refresh]);

  useEffect(() => {
    refresh();
    return () => clearTimeout(settle.current);
  }, [refresh]);

  const running = activity && activity.status !== "ARCHIVED";
  useEffect(() => {
    if (!live || !running) return undefined;
    const id = setInterval(refresh, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [live, running, refresh]);

  /** Do something, then show the view it returns (or read it again). */
  const act = useCallback(
    async (fn) => {
      setError(null);
      try {
        const result = await fn(activityApi, sessionGuid, activity?.guid);
        if (result && result.guid) setActivity(result);
        else await refresh();
        return true;
      } catch (e) {
        setError(e.message);
        return false;
      }
    },
    [sessionGuid, activity?.guid, refresh]
  );

  const clock = useCallback(() => Date.now() + offset.current, []);

  return { activity, error, refresh, poke, act, clock, isLive: live, setActivity };
}

/** Seconds left on the open question's clock, or null without a timer. */
export const secondsLeft = (activity, now) => {
  if (!activity?.timer_seconds || activity.status !== "LIVE") return null;
  const started = new Date(`${activity.question_started_at}Z`).getTime();
  return Math.max(0, Math.ceil(activity.timer_seconds - (now - started) / 1000));
};

export const KIND_LABEL = {
  QUIZ_RACE: "Quiz race",
  SOLO_SOLVE: "Solo solve",
  ESCAPE_ROOM: "Escape room",
};

/** "0625/42 · 2.1" — how a question is spoken about in a staffroom. */
export const questionLabel = (q) =>
  [q.paper_code, [q.number, q.letter, q.letter_part].filter(Boolean).join(".")].filter(Boolean).join(" · ");

/** The options a multiple-choice question offers, from its answer format. */
export const optionsOf = (answerFormat) => {
  if (!answerFormat) return [];
  if (answerFormat.includes("[DISPLAY OPTIONS]")) {
    return ["A", "B", "C", "D"].map((label) => ({ label, text: "" }));
  }
  const out = [];
  const re = /\[(\w+)\](.*?)(?=\[\w+\]|$)/gs;
  let m;
  while ((m = re.exec(answerFormat)) !== null) out.push({ label: m[1], text: m[2].trim() });
  return out;
};
